import { fetchSolanaRpc } from "@/lib/solanaRpc";

/** Loose Solana address shape (base58); same heuristic as mint inputs elsewhere. */
const ADDR_LIKELY = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function looksLikeSolanaAddress(s: string): boolean {
  return ADDR_LIKELY.test(s.trim());
}

type RpcBalance = {
  jsonrpc: string;
  id: number;
  result?: { context?: { slot?: number }; value?: number };
  error?: { message?: string };
};

/** Native SOL balance for an account (lamports → SOL). */
export async function fetchNativeSolBalance(pubkey: string): Promise<number | null> {
  const pk = pubkey.trim();
  if (!pk) return null;
  try {
    const res = await fetchSolanaRpc({ jsonrpc: "2.0", id: 1, method: "getBalance", params: [pk] });
    if (!res.ok) return null;
    const data = (await res.json()) as RpcBalance;
    if (data.error != null) return null;
    const lamports = data.result?.value;
    if (typeof lamports !== "number" || !Number.isFinite(lamports)) return null;
    return lamports / 1e9;
  } catch {
    return null;
  }
}

export async function fetchSplTokenBalance(walletPubkey: string, mintPubkey: string): Promise<number | null> {
  const owner = walletPubkey.trim();
  const mint = mintPubkey.trim();
  if (!owner || !mint) return null;
  try {
    const res = await fetchSolanaRpc({
      jsonrpc: "2.0",
      id: 1,
      method: "getTokenAccountsByOwner",
      params: [
        owner,
        { mint: mint },
        { encoding: "jsonParsed" }
      ]
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.error != null) return null;
    
    const accounts = data.result?.value;
    if (!Array.isArray(accounts) || accounts.length === 0) return 0;
    
    // Sum up balances from all token accounts for this mint
    let totalUiAmount = 0;
    for (const acc of accounts) {
      const uiAmount = acc.account?.data?.parsed?.info?.tokenAmount?.uiAmount;
      if (typeof uiAmount === "number") {
        totalUiAmount += uiAmount;
      }
    }
    return totalUiAmount;
  } catch {
    return null;
  }
}

export async function fetchAllSplTokensForOwner(walletPubkey: string): Promise<Array<{ mint: string; balance: number; decimals: number }> | null> {
  const owner = walletPubkey.trim();
  if (!owner) return null;
  try {
    const res = await fetchSolanaRpc({
      jsonrpc: "2.0",
      id: 1,
      method: "getTokenAccountsByOwner",
      params: [
        owner,
        { programId: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" },
        { encoding: "jsonParsed" }
      ]
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.error != null) return null;
    
    const accounts = data.result?.value;
    if (!Array.isArray(accounts)) return [];
    
    const tokensMap = new Map<string, { balance: number; decimals: number }>();
    for (const acc of accounts) {
      const info = acc.account?.data?.parsed?.info;
      const mint = info?.mint;
      const uiAmount = info?.tokenAmount?.uiAmount;
      const decimals = info?.tokenAmount?.decimals ?? 6;
      if (mint && typeof uiAmount === "number" && uiAmount > 0) {
        const existing = tokensMap.get(mint) || { balance: 0, decimals };
        existing.balance += uiAmount;
        tokensMap.set(mint, existing);
      }
    }
    
    return Array.from(tokensMap.entries()).map(([mint, val]) => ({
      mint,
      balance: val.balance,
      decimals: val.decimals,
    }));
  } catch {
    return null;
  }
}
